import { tokens } from "@core/theme/tokens";
import { FieldError, RequiredMark } from "./FormLayout";

// SPEC-215/SPEC-216: `Field` (etiqueta + marca de obligatorio + slot de
// error) y los estilos `inputStyle`/`fieldStyle` se repetían idénticos en
// ProfileForm, UserForm y CatalogEntryForm (jscpd, quality-gate 2026-08-25) —
// extraídos acá porque los tres valores eran 100% iguales, no una
// coincidencia superficial. `catalogStyles.form.inputPadding` y
// `userStyles.form.fieldGap` seguían resolviendo a estos mismos literales,
// así que este movimiento no cambia ningún valor visual.
export const inputStyle = {
  width: "100%",
  boxSizing: "border-box" as const,
  padding: "11px 13px",
  borderRadius: 11,
  border: `1px solid ${tokens.surface.line2}`,
  // portal.field.background — admin.jsx:inp / DESIGN-TOKENS.md §1.7.
  background: "rgba(255,255,255,0.03)",
  color: tokens.text.primary,
  font: `400 13.5px ${tokens.font.ui}`,
};

/**
 * Estilo compartido de `<select>`, derivado de `inputStyle` (revisión en vivo
 * 2026-09-22, puntos #2 y #18). Es el que deben llevar los `<select>` que hoy
 * no tienen ninguno — p. ej. "Motivo de inactivación" en el modal de Miembros,
 * que se renderiza 100% nativo (fondo blanco, texto negro) por no tener estilo.
 *
 * Acá va solo el cromo del campo. El reseteo de `appearance`, la flecha propia
 * (el `chevron` de `core/icons/paths.ts` rotado 90°) y el espacio a su derecha
 * los aplica la regla global `select` de `core/theme/base.css` a TODOS los
 * `<select>` de la aplicación, lleven este estilo o `inputStyle` inline: por
 * eso no se repiten acá, para que no puedan derivar entre los dos puntos.
 */
export const selectStyle = {
  ...inputStyle,
  cursor: "pointer" as const,
};

export const fieldStyle = {
  display: "flex",
  flexDirection: "column" as const,
  gap: 8,
};

export function Field({
  label,
  required,
  children,
  error,
  hint,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  error?: string | undefined;
  // SPEC-212 §3.1 (A2, human-resolution-spec-212.json): ranura de ayuda del
  // campo, calcada de admin.jsx:83-91 (`hint` de `Field` del prototipo,
  // usado en "Tope por período" y "Vigente hasta"). ADITIVA: sin `hint` no
  // se renderiza ningún nodo extra ni cambia la altura del campo, para que
  // los baselines de las demás pantallas del portal que ya usan este `Field`
  // (ProfileForm, UserForm, CatalogEntryForm) no se muevan ni un píxel.
  hint?: string | undefined;
}) {
  return (
    <label style={fieldStyle}>
      <span
        style={{
          font: `600 12px ${tokens.font.ui}`,
          color: tokens.text.secondary,
        }}
      >
        {label}
        {required ? <RequiredMark /> : null}
      </span>
      {children}
      {hint ? (
        <div
          style={{
            font: `400 11px/1.45 ${tokens.font.ui}`,
            color: tokens.text.disabled,
            marginTop: 6,
          }}
        >
          {hint}
        </div>
      ) : null}
      {error ? <FieldError message={error} /> : null}
    </label>
  );
}
